import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import { getFinancialSummary, getTransactions } from './FinancialService';
import { FinancialSummaryResponse, TransactionResponse } from './types';

// ─── design tokens ────────────────────────────────────────────────────────────
const C = {
  needs: '#159b72',
  wants: '#EC4899',
  savings: '#3B82F6',
  red: '#ff454d',
  income: '#8B5CF6',
  blue: '#1475d1',
  muted: '#78869a',
  ink: '#17263d',
  wash: '#f4f7fb',
};

const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n);

const pct = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 100) : 0;

const fmtSubCat = (s: string) =>
  s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

// ─── Sub-category row ─────────────────────────────────────────────────────────
function SubCatRow({
  label,
  amount,
  count,
  bucketTotal,
  color,
}: {
  label: string;
  amount: number;
  count: number;
  bucketTotal: number;
  color: string;
}) {
  const share =
    bucketTotal > 0
      ? Math.min(Math.round((amount / bucketTotal) * 100), 100)
      : 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: C.ink }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: C.muted }}>
            {count} txn{count !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: C.muted }}>
            {share}%
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ color: C.ink }}>
            {gbp(amount)}
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={share}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: alpha(color, 0.1),
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 },
        }}
      />
    </Box>
  );
}

// ─── Bucket card ──────────────────────────────────────────────────────────────
interface BucketCardProps {
  label: string;
  color: string;
  actual: number;
  budget: number;
  targetPct: number;
  incomePct: number;
  subCats: { subCat: string; amount: number; count: number }[];
  loading: boolean;
  monthYear: string;
}

function BucketCard({
  label,
  color,
  actual,
  budget,
  targetPct,
  incomePct,
  subCats,
  loading,
  monthYear,
}: BucketCardProps) {
  const over = actual > budget && budget > 0;
  const barColor = over ? C.red : color;
  const barPct =
    budget > 0 ? Math.min(Math.round((actual / budget) * 100), 100) : 0;
  const remaining = budget - actual;

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={100} height={28} />
          <Skeleton
            variant="rectangular"
            height={8}
            sx={{ borderRadius: 4, my: 2 }}
          />
          <Skeleton
            variant="rectangular"
            height={60}
            sx={{ borderRadius: 1 }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderTop: `3px solid ${color}` }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: C.ink }}>
              {label}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.75,
                mt: 0.25,
              }}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ color: over ? C.red : C.ink, lineHeight: 1 }}
              >
                {gbp(actual)}
              </Typography>
              {budget > 0 && (
                <Typography variant="body2" sx={{ color: C.muted }}>
                  of {gbp(budget)}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Chip
              label={`${incomePct}% of income`}
              size="small"
              sx={{
                bgcolor: alpha(color, 0.1),
                color,
                fontWeight: 700,
                fontSize: '0.65rem',
                height: 20,
              }}
            />
            {targetPct > 0 && (
              <Typography
                variant="caption"
                sx={{ display: 'block', color: C.muted, mt: 0.5 }}
              >
                Target: {targetPct}%
              </Typography>
            )}
          </Box>
        </Box>

        {/* Progress bar */}
        {budget > 0 && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={barPct}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(barColor, 0.12),
                '& .MuiLinearProgress-bar': {
                  bgcolor: barColor,
                  borderRadius: 4,
                },
              }}
            />
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
            >
              <Typography variant="caption" sx={{ color: C.muted }}>
                {barPct}% used
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: over ? C.red : C.muted }}
              >
                {over
                  ? `${gbp(Math.abs(remaining))} over budget`
                  : `${gbp(remaining)} remaining`}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Sub-category breakdown */}
        {subCats.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {subCats.map((sc) => (
              <SubCatRow
                key={sc.subCat}
                label={fmtSubCat(sc.subCat)}
                amount={sc.amount}
                count={sc.count}
                bucketTotal={actual}
                color={color}
              />
            ))}
          </Box>
        ) : actual === 0 ? (
          <Typography variant="body2" sx={{ color: C.muted }}>
            No {label.toLowerCase()} transactions in {monthYear}.
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: C.muted }}>
            No sub-category breakdown available.
          </Typography>
        )}

        {/* Link to transactions */}
        {actual > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="text"
              href="/user/transactions"
              sx={{ color: C.blue, p: 0, fontWeight: 600, fontSize: '0.8rem' }}
            >
              View {label.toLowerCase()} transactions →
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Income card ──────────────────────────────────────────────────────────────
function IncomeCard({
  total,
  subCats,
  loading,
  monthYear,
}: {
  total: number;
  subCats: { subCat: string; amount: number; count: number }[];
  loading: boolean;
  monthYear: string;
}) {
  if (loading) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={80} height={28} />
          <Skeleton
            variant="rectangular"
            height={60}
            sx={{ borderRadius: 1, mt: 2 }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderTop: `3px solid ${C.income}` }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: C.ink }}>
              Income
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ color: C.ink, lineHeight: 1, mt: 0.25 }}
            >
              {gbp(total)}
            </Typography>
          </Box>
          <Chip
            label="Actuals only"
            size="small"
            sx={{
              bgcolor: alpha(C.income, 0.1),
              color: C.income,
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 20,
            }}
          />
        </Box>

        {subCats.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {subCats.map((sc) => (
              <SubCatRow
                key={sc.subCat}
                label={
                  sc.subCat === 'SALARY'
                    ? 'Salary'
                    : sc.subCat === 'INCOME_OTHER'
                      ? 'Other income'
                      : fmtSubCat(sc.subCat)
                }
                amount={sc.amount}
                count={sc.count}
                bucketTotal={total}
                color={C.income}
              />
            ))}
          </Box>
        ) : total === 0 ? (
          <Typography variant="body2" sx={{ color: C.muted }}>
            No income transactions in {monthYear}.
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: C.muted }}>
            No income breakdown available.
          </Typography>
        )}

        <Typography
          variant="caption"
          sx={{ display: 'block', color: C.muted, mt: 2 }}
        >
          Showing posted actuals only. User-entered assumptions and forecasts
          are not mixed into these figures.
        </Typography>
      </CardContent>
    </Card>
  );
}

// ─── SpendingAnalysisPage ─────────────────────────────────────────────────────
const SpendingAnalysisPage = (): JSX.Element => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [summary, setSummary] = useState<FinancialSummaryResponse | null>(null);
  const [transactions, setTransactions] = useState<TransactionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const monthLabel = new Date(year, month - 1).toLocaleString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    const next = new Date(year, month);
    if (next <= now) {
      if (month === 12) {
        setMonth(1);
        setYear((y) => y + 1);
      } else setMonth((m) => m + 1);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      getFinancialSummary(year, month),
      getTransactions(year, month),
    ])
      .then(([sumRes, txRes]) => {
        setSummary(sumRes.data ?? null);
        setTransactions(txRes.data ?? []);
      })
      .catch(() => setError('Could not load spending data.'))
      .finally(() => setLoading(false));
  }, [year, month]);

  // ─── Aggregations ────────────────────────────────────────────────────────────
  const { needsSubCats, wantsSubCats, savingsSubCats, incomeSubCats } =
    useMemo(() => {
      const active = transactions.filter((tx) => !tx.isExcluded);

      const aggregate = (cat: string, direction: 'DEBIT' | 'CREDIT') => {
        const rows = active.filter(
          (tx) => tx.category === cat && tx.direction === direction,
        );
        const map = new Map<string, { amount: number; count: number }>();
        for (const tx of rows) {
          const key = tx.subCategory ?? cat;
          const prev = map.get(key) ?? { amount: 0, count: 0 };
          map.set(key, {
            amount: prev.amount + Math.abs(tx.amount),
            count: prev.count + 1,
          });
        }
        return Array.from(map.entries())
          .map(([subCat, v]) => ({ subCat, ...v }))
          .sort((a, b) => b.amount - a.amount);
      };

      const aggregateIncome = () => {
        const rows = active.filter((tx) => tx.direction === 'CREDIT');
        const map = new Map<string, { amount: number; count: number }>();
        for (const tx of rows) {
          const key = tx.category;
          const prev = map.get(key) ?? { amount: 0, count: 0 };
          map.set(key, {
            amount: prev.amount + Math.abs(tx.amount),
            count: prev.count + 1,
          });
        }
        return Array.from(map.entries())
          .map(([subCat, v]) => ({ subCat, ...v }))
          .sort((a, b) => b.amount - a.amount);
      };

      return {
        needsSubCats: aggregate('NEEDS', 'DEBIT'),
        wantsSubCats: aggregate('WANTS', 'DEBIT'),
        savingsSubCats: aggregate('SAVINGS', 'DEBIT'),
        incomeSubCats: aggregateIncome(),
      };
    }, [transactions]);

  const income = summary?.income ?? 0;
  const needsActual = summary?.needs ?? 0;
  const wantsActual = summary?.wants ?? 0;
  const savingsActual = summary?.savings ?? 0;
  const needsTarget = summary?.needsTarget ?? 50;
  const wantsTarget = summary?.wantsTarget ?? 30;
  const savingsTarget = summary?.savingsTarget ?? 20;

  const needsBudget = (income * needsTarget) / 100;
  const wantsBudget = (income * wantsTarget) / 100;
  const savingsBudget = (income * savingsTarget) / 100;

  const totalSpend = needsActual + wantsActual + savingsActual;
  const netPosition = income - totalSpend;

  return (
    <UserDashboard>
      <Box
        sx={{
          background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Header + month nav */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
                Spending Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: C.muted }}>
                Income, spending and 50/30/20 breakdown — these are posted
                actuals, not forecasts
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <IconButton
                size="small"
                onClick={prevMonth}
                aria-label="Previous month"
                sx={{ color: C.muted, '&:hover': { color: C.ink } }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: C.ink, minWidth: 130, textAlign: 'center' }}
              >
                {monthLabel}
              </Typography>
              <IconButton
                size="small"
                onClick={nextMonth}
                disabled={new Date(year, month) > now}
                aria-label="Next month"
                sx={{ color: C.muted, '&:hover': { color: C.ink } }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {error && (
            <Card sx={{ mb: 3, border: `1px solid ${alpha(C.red, 0.3)}` }}>
              <CardContent>
                <Typography color="error">{error}</Typography>
              </CardContent>
            </Card>
          )}

          {/* KPI row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[
              { label: 'Income', value: income, color: undefined },
              { label: 'Total Spend', value: totalSpend, color: undefined },
              {
                label: 'Net Position',
                value: netPosition,
                color: netPosition >= 0 ? C.needs : C.red,
              },
            ].map((kpi) => (
              <Grid item xs={12} sm={4} key={kpi.label}>
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: C.muted,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {kpi.label}
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width={120} height={52} />
                    ) : (
                      <Typography
                        variant="h3"
                        fontWeight={700}
                        sx={{ mt: 0.5, color: kpi.color ?? C.ink }}
                      >
                        {gbp(kpi.value)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Guidance note */}
          <Card
            sx={{
              mb: 3,
              bgcolor: alpha(C.blue, 0.04),
              border: `1px solid ${alpha(C.blue, 0.12)}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="body2" sx={{ color: C.ink }}>
                <strong>About the 50/30/20 split:</strong> This is a widely-used
                guide — not a universal rule. The right allocation depends on
                your income, household, commitments and goals. Your configured
                targets are shown per bucket below. Adjust them in your
                financial profile.
              </Typography>
            </CardContent>
          </Card>

          {/* Income + spending bucket cards */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <IncomeCard
                total={income}
                subCats={incomeSubCats}
                loading={loading}
                monthYear={monthLabel}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <BucketCard
                label="Needs"
                color={C.needs}
                actual={needsActual}
                budget={needsBudget}
                targetPct={needsTarget}
                incomePct={pct(needsActual, income)}
                subCats={needsSubCats}
                loading={loading}
                monthYear={monthLabel}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <BucketCard
                label="Wants"
                color={C.wants}
                actual={wantsActual}
                budget={wantsBudget}
                targetPct={wantsTarget}
                incomePct={pct(wantsActual, income)}
                subCats={wantsSubCats}
                loading={loading}
                monthYear={monthLabel}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <BucketCard
                label="Savings"
                color={C.savings}
                actual={savingsActual}
                budget={savingsBudget}
                targetPct={savingsTarget}
                incomePct={pct(savingsActual, income)}
                subCats={savingsSubCats}
                loading={loading}
                monthYear={monthLabel}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </UserDashboard>
  );
};

export default SpendingAnalysisPage;
